// components/Admin/EmoticonManager/EmoticonLogTable.jsx
import React, { useEffect, useState, useMemo } from 'react';
import {
    Box, Stack, TextField, Typography, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Pagination,
    useTheme, useMediaQuery, Divider,
} from '@mui/material';
import api from '../../../api/axios';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';

const EmoticonLogTable = () => {
    const [logs, setLogs] = useState([]);
    const [search, setSearch] = useState('');
    const [selectedDate, setSelectedDate] = useState('');
    const [page, setPage] = useState(1);

    const rowsPerPage = 10;

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        api.get('/api/management/emoticon-logs/')
            .then(res => setLogs(res.data || []))
            .catch(err => console.error('로그 불러오기 실패', err));
    }, []);

    const filteredLogs = useMemo(() => (
        logs.filter(log => {
            const keyword = search.toLowerCase();
            const matchesKeyword =
                (log.user || '').toLowerCase().includes(keyword) ||
                (log.emoticon || '').toLowerCase().includes(keyword);

            const matchesDate = selectedDate
                ? (log.purchased_at || '').startsWith(selectedDate)
                : true;

            return matchesKeyword && matchesDate;
        })
    ), [logs, search, selectedDate]);

    const totalPages = Math.max(1, Math.ceil(filteredLogs.length / rowsPerPage));
    const paginated = filteredLogs.slice(
        (page - 1) * rowsPerPage,
        page * rowsPerPage
    );

    const handleExportExcel = () => {
        const rows = filteredLogs.map((log) => ({
            ID: log.id,
            구매자: log.user,
            이모티콘: log.emoticon,
            가격: log.point_spent,
            구매일시: dayjs(log.purchased_at).format('YYYY-MM-DD HH:mm'),
        }));

        if (!rows.length) {
            alert('엑셀로 내보낼 로그가 없습니다.');
            return;
        }

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'EmoticonLogs');

        const today = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(workbook, `emoticon_logs_${today}.xlsx`);
    };

    return (
        <Box sx={{ width: '100%' }}>
            {/* 🔎 필터 바 */}
            <Stack
                direction={isMobile ? 'column' : 'row'}
                spacing={2}
                sx={{ mb: 2, width: '100%' }}
                flexWrap="wrap"
                alignItems={isMobile ? 'stretch' : 'center'}
            >
                <TextField
                    size="small"
                    label="검색 (구매자 / 이모티콘명)"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    fullWidth={isMobile}
                />

                <TextField
                    type="date"
                    size="small"
                    label="날짜"
                    InputLabelProps={{ shrink: true }}
                    value={selectedDate}
                    onChange={(e) => { setSelectedDate(e.target.value); setPage(1); }}
                    fullWidth={isMobile}
                />

                {/* ✅ PC: 우측 액션 영역으로 묶기 / 모바일: 기존처럼 100% */}
                {isMobile ? (
                    <>
                        <Typography
                            variant="body2"
                            sx={{ textAlign: 'right', width: '100%' }}
                        >
                            총 {filteredLogs.length}건
                        </Typography>

                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleExportExcel}
                            fullWidth
                        >
                            📥 엑셀로 내보내기
                        </Button>
                    </>
                ) : (
                    <Box
                        sx={{
                            ml: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.secondary',
                                whiteSpace: 'nowrap',   // ⭐ 핵심
                            }}
                        >
                            총 {filteredLogs.length}건
                        </Typography>

                        <Button
                            variant="contained"   // ✅ PC에서 더 “액션 버튼”처럼 보임 (원하면 outlined로 돌려도 됨)
                            size="small"
                            onClick={handleExportExcel}
                        >
                            📥 엑셀로 내보내기
                        </Button>
                    </Box>
                )}
            </Stack>

            {/* ✅ 모바일 = 카드형 / PC = 테이블 */}
            {isMobile ? (
                <Stack spacing={1.25} sx={{ width: '100%' }}>
                    {paginated.length === 0 ? (
                        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                            <Typography sx={{ color: 'text.secondary' }}>
                                🔍 조건에 맞는 로그가 없습니다.
                            </Typography>
                        </Paper>
                    ) : (
                        paginated.map((log) => (
                            <Paper key={log.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                                <Stack spacing={0.75}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="body2" color="text.secondary">구매자</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                            {log.user}
                                        </Typography>
                                    </Stack>

                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="body2" color="text.secondary">이모티콘</Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 700, maxWidth: '65%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                        >
                                            {log.emoticon}
                                        </Typography>
                                    </Stack>

                                    <Divider />

                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="body2" color="text.secondary">가격</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                            {(log.point_spent ?? 0).toLocaleString()} Leaf🌿
                                        </Typography>
                                    </Stack>

                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="body2" color="text.secondary">구매일시</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                            {dayjs(log.purchased_at).format('YYYY-MM-DD HH:mm')}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Paper>
                        ))
                    )}

                    {totalPages > 1 && (
                        <Box mt={0.5} display="flex" justifyContent="center">
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(_, v) => setPage(v)}
                                color="primary"
                                showFirstButton
                                showLastButton
                                size="small"
                                shape="rounded"
                            />
                        </Box>
                    )}
                </Stack>
            ) : (
                <>
                    <TableContainer
                        component={Paper}
                        sx={{ borderRadius: 2, overflowX: 'auto' }}
                    >
                        <Table size="medium">
                            <TableHead>
                                <TableRow>
                                    <TableCell>구매자</TableCell>
                                    <TableCell>이모티콘</TableCell>
                                    <TableCell>가격</TableCell>
                                    <TableCell>구매일시</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginated.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                                            🔍 조건에 맞는 로그가 없습니다.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginated.map((log) => (
                                        <TableRow key={log.id}>
                                            <TableCell>{log.user}</TableCell>
                                            <TableCell>{log.emoticon}</TableCell>
                                            <TableCell>{(log.point_spent ?? 0).toLocaleString()} Leaf🌿</TableCell>
                                            <TableCell>
                                                {dayjs(log.purchased_at).format('YYYY-MM-DD HH:mm')}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {totalPages > 1 && (
                        <Box mt={2} display="flex" justifyContent="center">
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(_, v) => setPage(v)}
                                color="primary"
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default EmoticonLogTable;
